import React from "react";
import {
    Card, CardBody, Container, Row, Col, Table,
    Badge, Button, Spinner, Alert, Input, Label,
    FormGroup, Modal, ModalHeader, ModalBody, ModalFooter
} from "reactstrap";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { sessionEnded } from "../user/SessionEnded";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";
import {
    adminGetSongFlowDetail,
    adminUpdateSongFlowPublishing,
    adminListRecipients,
    adminSendSongFlow
} from "../../components/helpers/PublicCuratedSongFlowHelpers";

class AdminCuratedSongFlowDetail extends React.Component {
    state = {
        unauthorized: false,
        isLoading: true,
        songFlow: null,
        recipients: [],
        selectedIds: new Set(),
        isSaving: false,
        isSending: false,
        sendResult: null,
        sendError: "",
        confirmSendAll: false,
        statusFilter: "ACTIVE",
    };

    componentDidMount() {
        try {
            const customer = sessionStorage.getItem("customer");
            const parsed = customer ? JSON.parse(customer) : null;
            if (!parsed?.user?.isAdmin) {
                this.setState({ unauthorized: true });
                return;
            }
        } catch (e) {
            this.setState({ unauthorized: true });
            return;
        }
        this.loadData();
    }

    async loadData() {
        const { playlistId } = this.props.params;
        const [sf, recipients] = await Promise.all([
            adminGetSongFlowDetail(playlistId),
            adminListRecipients("ACTIVE")
        ]);
        this.setState({ songFlow: sf, recipients: recipients || [], isLoading: false });
    }

    async loadRecipients(status) {
        const recipients = await adminListRecipients(status || null);
        this.setState({ recipients: recipients || [], statusFilter: status || "" });
    }

    handleToggleSelect(id) {
        this.setState(prev => {
            const next = new Set(prev.selectedIds);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return { selectedIds: next };
        });
    }

    handleSelectAll() {
        const { recipients, selectedIds } = this.state;
        const activeIds = recipients.filter(r => r.status === "ACTIVE").map(r => r.id);
        if (selectedIds.size === activeIds.length) {
            this.setState({ selectedIds: new Set() });
        } else {
            this.setState({ selectedIds: new Set(activeIds) });
        }
    }

    async handlePublishToggle() {
        const { songFlow } = this.state;
        const { playlistId } = this.props.params;
        this.setState({ isSaving: true });
        const result = await adminUpdateSongFlowPublishing(
            playlistId,
            songFlow.published !== false,
            !songFlow.published
        );
        if (result) {
            await this.loadData();
        }
        this.setState({ isSaving: false });
    }

    async handleSendSelected() {
        const { selectedIds } = this.state;
        const { playlistId } = this.props.params;
        if (selectedIds.size === 0) return;
        this.setState({ isSending: true, sendResult: null, sendError: "" });
        const result = await adminSendSongFlow(playlistId, false, Array.from(selectedIds));
        if (result) {
            this.setState({ sendResult: result, isSending: false, selectedIds: new Set() });
            await this.loadData();
        } else {
            this.setState({ sendError: "Send failed. Please try again.", isSending: false });
        }
    }

    async handleSendAll() {
        const { playlistId } = this.props.params;
        this.setState({ isSending: true, sendResult: null, sendError: "", confirmSendAll: false });
        const result = await adminSendSongFlow(playlistId, true, []);
        if (result) {
            this.setState({ sendResult: result, isSending: false });
            await this.loadData();
        } else {
            this.setState({ sendError: "Send failed. Please try again.", isSending: false });
        }
    }

    copyPublicUrl() {
        const { songFlow } = this.state;
        if (!songFlow?.publicSlug) return;
        const url = `${window.location.origin}/song-flows/${songFlow.publicSlug}`;
        navigator.clipboard?.writeText(url);
    }

    openPublicUrl() {
        const { songFlow } = this.state;
        if (!songFlow?.publicSlug) return;
        const url = `/song-flows/${songFlow.publicSlug}`;
        window.open(url, "_blank");
    }

    formatDate(dt) {
        if (!dt) return "—";
        return new Date(dt).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    }

    render() {
        if (!sessionStorage.getItem("customer")) return sessionEnded();
        if (this.state.unauthorized) return <Navigate to="/" />;

        const {
            isLoading, songFlow, recipients, selectedIds,
            isSaving, isSending, sendResult, sendError,
            confirmSendAll, statusFilter
        } = this.state;
        const { navigate } = this.props;

        const activeRecipients = recipients.filter(r => r.status === "ACTIVE");
        const allActiveSelected = activeRecipients.length > 0 && selectedIds.size === activeRecipients.length;

        return (
            <>
                <SeoNoIndex noindex={true} />

                <div className="position-relative">
                    <section className="section section-lg section-shaped pb-250">
                        <div className="shape shape-style-1 shape-default">
                            <span /><span /><span /><span /><span /><span /><span /><span /><span />
                        </div>
                        <Container className="py-lg-md d-flex">
                            <div className="col px-0">
                                <Row>
                                    <Col lg="10">
                                        <Button color="link" className="text-white pl-0 mb-2" onClick={() => navigate("/admin/curated-song-flows")}>
                                            &larr; Back to song-flows
                                        </Button>
                                        <h1 className="display-3 text-white">
                                            {isLoading ? "Loading…" : (songFlow?.name || "Song-flow")}
                                        </h1>
                                        {songFlow?.notes && (
                                            <p className="lead text-white">{songFlow.notes}</p>
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        </Container>
                        <div className="separator separator-bottom separator-skew">
                            <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                                <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                            </svg>
                        </div>
                    </section>
                </div>

                <section className="section section-lg pt-lg-0 mt--200">
                    <Container>
                        {isLoading ? (
                            <div className="text-center py-5"><Spinner color="primary" /></div>
                        ) : (
                            <>
                                {/* Publishing controls */}
                                <Row className="mb-4">
                                    <Col lg="12">
                                        <Card className="shadow border-0">
                                            <CardBody className="py-4 px-4">
                                                <Row className="align-items-center">
                                                    <Col md="6">
                                                        <h5 className="mb-1">Publishing</h5>
                                                        <div className="d-flex align-items-center mb-2">
                                                            {songFlow?.published
                                                                ? <Badge color="success" className="mr-2">Published</Badge>
                                                                : <Badge color="secondary" className="mr-2">Unpublished</Badge>}
                                                            {songFlow?.publishedAt && (
                                                                <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                                                                    Published {this.formatDate(songFlow.publishedAt)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {songFlow?.publicSlug && (
                                                            <div style={{ fontSize: "0.8rem", color: "#525f7f", wordBreak: "break-all" }}>
                                                                <strong>Slug:</strong> {songFlow.publicSlug}
                                                            </div>
                                                        )}
                                                    </Col>
                                                    <Col md="6" className="text-md-right mt-3 mt-md-0">
                                                        {songFlow?.publicSlug && (
                                                            <>
                                                                <Button size="sm" color="secondary" outline className="mr-2" onClick={() => this.copyPublicUrl()}>
                                                                    Copy URL
                                                                </Button>
                                                                <Button size="sm" color="secondary" outline className="mr-2" onClick={() => this.openPublicUrl()}>
                                                                    Open
                                                                </Button>
                                                            </>
                                                        )}
                                                        <Button
                                                            size="sm"
                                                            color={songFlow?.published ? "warning" : "success"}
                                                            disabled={isSaving}
                                                            onClick={() => this.handlePublishToggle()}
                                                        >
                                                            {isSaving ? <Spinner size="sm" /> : (songFlow?.published ? "Unpublish" : "Publish")}
                                                        </Button>
                                                    </Col>
                                                </Row>
                                                <Row className="mt-3">
                                                    <Col>
                                                        <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                                                            <strong>Active subscribers:</strong> {songFlow?.subscriberCount || 0}
                                                            {songFlow?.lastEmailedAt && (
                                                                <span className="ml-3">
                                                                    <strong>Last emailed:</strong> {this.formatDate(songFlow.lastEmailedAt)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Send result */}
                                {sendResult && (
                                    <Row className="mb-3">
                                        <Col>
                                            <Alert color="success">
                                                Send complete — attempted: <strong>{sendResult.attempted}</strong>,
                                                sent: <strong>{sendResult.sent}</strong>,
                                                skipped: <strong>{sendResult.skipped}</strong>,
                                                failed: <strong>{sendResult.failed}</strong>
                                            </Alert>
                                        </Col>
                                    </Row>
                                )}
                                {sendError && (
                                    <Row className="mb-3">
                                        <Col><Alert color="danger">{sendError}</Alert></Col>
                                    </Row>
                                )}

                                {/* Recipients */}
                                <Row>
                                    <Col lg="12">
                                        <Card className="shadow border-0">
                                            <CardBody className="py-4 px-4">
                                                <Row className="align-items-center mb-3">
                                                    <Col md="6">
                                                        <h5 className="mb-0">Recipients</h5>
                                                    </Col>
                                                    <Col md="6" className="text-md-right mt-2 mt-md-0">
                                                        <Input
                                                            type="select"
                                                            bsSize="sm"
                                                            style={{ display: "inline-block", width: "auto", marginRight: "8px" }}
                                                            value={statusFilter}
                                                            onChange={e => this.loadRecipients(e.target.value)}
                                                        >
                                                            <option value="ACTIVE">Active</option>
                                                            <option value="UNSUBSCRIBED">Unsubscribed</option>
                                                            <option value="">All</option>
                                                        </Input>
                                                        <Button
                                                            size="sm"
                                                            color="primary"
                                                            outline
                                                            disabled={selectedIds.size === 0 || isSending || !songFlow?.published}
                                                            onClick={() => this.handleSendSelected()}
                                                            className="mr-2"
                                                        >
                                                            {isSending ? <Spinner size="sm" /> : `Send selected (${selectedIds.size})`}
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            color="primary"
                                                            disabled={isSending || activeRecipients.length === 0 || !songFlow?.published}
                                                            onClick={() => this.setState({ confirmSendAll: true })}
                                                        >
                                                            Share to all ({activeRecipients.length})
                                                        </Button>
                                                    </Col>
                                                </Row>

                                                {!songFlow?.published && (
                                                    <Alert color="warning" className="mb-3">
                                                        Publish this song-flow before sending.
                                                    </Alert>
                                                )}

                                                {recipients.length === 0 ? (
                                                    <p className="text-muted">No recipients found.</p>
                                                ) : (
                                                    <Table responsive hover size="sm">
                                                        <thead>
                                                            <tr>
                                                                <th style={{ width: "40px" }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={allActiveSelected}
                                                                        onChange={() => this.handleSelectAll()}
                                                                        style={{ width: "16px", height: "16px", accentColor: "#5e72e4" }}
                                                                    />
                                                                </th>
                                                                <th>Email</th>
                                                                <th>Name</th>
                                                                <th>Status</th>
                                                                <th>Signed up</th>
                                                                <th>Last emailed</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {recipients.map(r => (
                                                                <tr key={r.id}>
                                                                    <td>
                                                                        {r.status === "ACTIVE" && (
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedIds.has(r.id)}
                                                                                onChange={() => this.handleToggleSelect(r.id)}
                                                                                style={{ width: "16px", height: "16px", accentColor: "#5e72e4" }}
                                                                            />
                                                                        )}
                                                                    </td>
                                                                    <td>{r.email}</td>
                                                                    <td>{r.name || "—"}</td>
                                                                    <td>
                                                                        {r.status === "ACTIVE"
                                                                            ? <Badge color="success">Active</Badge>
                                                                            : <Badge color="secondary">Unsubscribed</Badge>}
                                                                    </td>
                                                                    <td style={{ fontSize: "0.8rem" }}>{this.formatDate(r.createdAt)}</td>
                                                                    <td style={{ fontSize: "0.8rem" }}>{this.formatDate(r.lastEmailedAt)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                )}
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </Container>
                </section>

                {/* Confirm send-all modal */}
                <Modal isOpen={confirmSendAll} toggle={() => this.setState({ confirmSendAll: false })}>
                    <ModalHeader toggle={() => this.setState({ confirmSendAll: false })}>
                        Share to all subscribers?
                    </ModalHeader>
                    <ModalBody>
                        This will send <strong>{songFlow?.name}</strong> to all{" "}
                        <strong>{activeRecipients.length}</strong> active subscriber{activeRecipients.length !== 1 ? "s" : ""}.
                        Are you sure?
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" onClick={() => this.handleSendAll()} disabled={isSending}>
                            {isSending ? <Spinner size="sm" /> : "Yes, send to all"}
                        </Button>
                        <Button color="secondary" outline onClick={() => this.setState({ confirmSendAll: false })}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Modal>
            </>
        );
    }
}

function withRouterParamsAndNavigate(Component) {
    return function Wrapped(props) {
        const navigate = useNavigate();
        const params = useParams();
        return <Component {...props} navigate={navigate} params={params} />;
    };
}

export default withRouterParamsAndNavigate(AdminCuratedSongFlowDetail);
